<template>
  <div>
    <submit-section-modal
      v-if="showModal"
      @close-modal="closeModal"
      @submit-section="handleSubmitSection"
    >
      <p>
        Are you sure you wish to continue?<br />
        You cannot go back.
      </p>
    </submit-section-modal>
    <div class="section-review-border">
      <div class="section-review-header">
        <span
          v-if="isSimulationComplete"
          class="inline-row"
        >
          <span>{{ sectionTally.correct }}/{{ flashcards.length }}</span>
          <button
            class="button-icon"
            tabindex="0"
            title="Return to Performance"
            @click="goToPerformance"
            @keydown.enter="goToPerformance"
          >
            <icon
              name="arrow-in-box"
              class="icon"
            ></icon>
          </button>
        </span>
        <span
          v-else
        >
          {{ formattedTime(timeLeftInSeconds) }}
        </span>
      </div>
      <data-loader>
        <div
          class="section-review-content"
        >
          <h1>SECTION REVIEW</h1>
          <div class="box-divider"></div>
          <div class="section-review-instructions">
            <div><strong>Below is a summary of your answers.</strong></div>
            <div>The buttons in the lower left corner have three functions:</div>
            <ol>
              <li>Review all of your questions answered, incomplete and flagged.</li>
              <li>Review questions that are incomplete.</li>
              <li>Review questions that are flagged.</li>
            </ol>
          </div>
          <div class="box-divider">
            <span>Section {{ sectionIndex + 1 }} questions</span>
            <span
              v-if="isSimulationComplete"
            >
              ({{ flashcardListStatuses.length }} Questions, {{ sectionTally.correct }} Correct, {{ flashcardListStatuses.length - sectionTally.correct }} Incorrect, {{ flaggedCount }} Flagged)
            </span>
            <span
              v-else
            >({{ flashcardListStatuses.length }} Questions, {{ answeredCount }} Complete, {{ flashcardListStatuses.length - answeredCount }} Incomplete, {{ flaggedCount }} Flagged)</span>
          </div>
          <div class="section-review-container">
            <section-review-column
              :flashcard-list-statuses="column1"
              :is-simulation-complete="isSimulationComplete"
              @navigate="navigate"
            />
            <section-review-column
              :flashcard-list-statuses="column2"
              :first-question-number="startColumn2 + 1"
              :is-simulation-complete="isSimulationComplete"
              @navigate="navigate"
            />
            <section-review-column
              :flashcard-list-statuses="column3"
              :first-question-number="startColumn3 + 1"
              :is-simulation-complete="isSimulationComplete"
              @navigate="navigate"
            />
          </div>
        </div>
      </data-loader>
      <div class="section-review-footer">
        <section-review-footer
          :is-simulation-complete="isSimulationComplete"
          @review="startReview"
          @submit-section="openModal"
          @return-to-performance="goToPerformance"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SectionReviewFooter from '@components/sims/sectionReview/SectionReviewFooter.vue';
import SectionReviewColumn from '@components/sims/sectionReview/SectionReviewColumn.vue';
import SubmitSectionModal from '@components/sims/sectionReview/SubmitSectionModal.vue';
import WuxSimulationsMixin from '@/mixins/wux-simulations';
import DataLoader from '@/components/sims/DataLoader.vue';
import Icon from '@components/singletons/icon.vue';

export default {
  components: {
    DataLoader,
    Icon,
    SectionReviewFooter,
    SectionReviewColumn,
    SubmitSectionModal,
  },
  mixins: [WuxSimulationsMixin],
  data() {
    return {
      showModal: false,
      timeLeftInSeconds: null,
      timerInterval: null,
    };
  },
  computed: {
    answeredCount() {
      return this.flashcardListStatuses.filter( status => status.isAnswered).length;
    },
    column1() {
      return this.flashcardListStatuses.slice(0, this.startColumn2);
    },
    column2() {
      return this.flashcardListStatuses.slice(this.startColumn2, this.startColumn3);
    },
    column3() {
      return this.flashcardListStatuses.slice(this.startColumn3);
    },
    flaggedCount() {
      return this.flashcardListStatuses.filter( status => status.isBookmarked).length;
    },
    flashcardSetIds() {
      return this.$store.getters['flashcardSet/ids'];
    },
    startColumn2() {
      return Math.ceil(this.flashcardListStatuses.length / 3);
    },
    startColumn3() {
      return Math.ceil(this.flashcardListStatuses.length / 3 * 2);
    },
  },
  watch: {
    timeLeftInSeconds(currentValue) {
      if (currentValue < 0) {
        this.handleSubmitSection();
      }
    },
  },
  mounted() {
    if (this.isSimulationComplete) {
      return;
    }

    this.timerInterval = setInterval(() => {
      try {
        this.timerTick(this.inProgressStatus, this.section.timeInMinutes);
      }
      catch (e) {
        clearInterval(this.timerInterval);
      }
    }, 250);
  },
  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  methods: {
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    handleSubmitSection() {
      if (this.isSimulationComplete) {
        return;
      }

      clearInterval(this.timerInterval);
      this.closeModal();
      this.submitSection();
    },
    goToPerformance() {
      this.$router.push({
        name: 'sims#performance',
        params: {
          simId: this.simId,
        },
      });
    },
    navigate(flashcardId, filter) {
      return this.$router.push({
        name: 'simSectionFlashcard#show',
        params: {
          simId: this.simId,
          sectionId: this.sectionId,
          flashcardId: flashcardId,
        },
        query: {
          filter,
        },
      });
    },
    formatQuestionNumber(flashcardId) {
      if (flashcardId >= 9) {
        return `${flashcardId + 1}`;
      }
      return `0${flashcardId + 1}`;
    },
    startReview(filter) {
      let flashcardId = this.flashcardListStatuses[0].id;

      if (filter === 'incomplete') {
        this.$store.dispatch('flashcardSet/setIds',
          this.flashcardListStatuses.filter(status => !status.isAnswered).map(status => status.id),
        );
        flashcardId = this.$store.getters['flashcardSet/ids'][0];
      }

      if (filter === 'flagged') {
        this.$store.dispatch('flashcardSet/setIds',
          this.flashcardListStatuses.filter(status => status.isBookmarked).map(status => status.id),
        );
        flashcardId = this.$store.getters['flashcardSet/ids'][0];
      }

      if (filter === 'incorrect') {
        this.$store.dispatch('flashcardSet/setIds',
          this.flashcardListStatuses.filter(
            status => (status.isAnswered && !status.isCorrect) || !status.isAnswered,
          ).map(status => status.id),
        );
        flashcardId = this.$store.getters['flashcardSet/ids'][0];
      }

      if (!flashcardId) return;
      // When falsy, it's important to pass undefined so that the query string will be empty
      this.navigate(flashcardId, filter ? true : undefined);
    },
  },
};
</script>

<style lang='scss' scoped>
$sim-background-color: #00629E;
$header-height: 4.5rem;
$border-width: 1rem;
$footer-height: 4.5rem;

.section-review-border {
  margin: 0;
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  z-index: 1000;
  background-color: $sim-background-color;

  .section-review-content {
    position: fixed;
    top: $header-height;
    left: $border-width;
    right: $border-width;
    bottom: $footer-height;
    background-color: white;
    overflow: auto;
    padding: 0 1.5rem;

    h1 {
      font-size: 20px;
      font-weight: 700;
      margin-block-start: 1.5rem;
      margin-block-end: 1.5rem;
      text-align: center;
    }

    .box-divider {
      height: 2rem;
      background-color: $sim-background-color;
      margin: 1.5rem 0;
      font-size: 14px;
      font-weight: 600;
      color: white;
      line-height: 2rem;
      display: flex;
      justify-content: space-between;
      padding: 0 1.25rem;
    }

    ol {
      margin-block-start: 0;
      margin-block-end: 0;
      padding-inline-start: 1.5rem;
    }

    .section-review-container {
      margin-top: 3rem;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
    }

    .section-review-instructions {
      line-height: 1.5rem;
    }
  }

  .section-review-header {
    line-height: 4.5rem;
    font-size: 1.25rem;
    font-weight: 600;
    padding-right: 2.5rem;
    color: white;
    display: flex;
    justify-content: flex-end;
  }

  .section-review-footer {
    position: fixed;
    color: white;
    background-color: $sim-background-color;
    bottom: 0px;
    left: 0px;
    right: 0px;
    height: $footer-height;
  }
}

.button-icon {
  background-color: transparent;
  padding: 0;
  margin: 0;
  line-height: 0;
}

.icon {
  width: 1.5rem;
  height: 1.5rem;
}

.inline-row {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

</style>
